import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { featureLabel } from "../lib/features";
import { Timer, X, ArrowRight } from "@phosphor-icons/react";

const LAST_DAYS = 3;

/** Amber strip during the last 3 days of the workspace's own free trial, or of an add-on free trial. Data comes from /auth/me (`dunning`, `addon_trials`). */
export default function TrialBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() => (sessionStorage.getItem("fixli_trial_banner_dismissed") || "").split(",").filter(Boolean));
  if (!user || user.is_platform_admin) return null;
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const ws = user.dunning;
  if (ws?.reason === "trial" && ws.days_left != null && ws.days_left <= LAST_DAYS && !dismissed.includes("__workspace__")) {
    const when = ws.trial_ends_at ? new Date(ws.trial_ends_at).toLocaleDateString("en-GB") : "";
    const days = ws.days_left <= 0 ? "today" : ws.days_left === 1 ? "tomorrow" : `in ${ws.days_left} days`;
    return (
      <div className="bg-amber-500 text-amber-950 text-xs sm:text-sm px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="workspace-trial-banner">
        <Timer size={16} weight="bold" className="shrink-0" />
        <span className="flex-1 min-w-0">
          <strong>{user.tenant_name || "Your workspace"}</strong>'s free trial ends {days} ({when}). After that it becomes read-only until it's subscribed — nothing is lost.
          {isAdmin ? "" : " Ask your workspace admin to subscribe."}
        </span>
        {isAdmin && (
          <Link to="/platform/billing" className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline" data-testid="workspace-trial-banner-cta">
            Subscribe <ArrowRight size={12} weight="bold" />
          </Link>
        )}
        <button type="button" className="p-1 rounded hover:bg-amber-600/40" aria-label="Dismiss" data-testid="workspace-trial-banner-dismiss"
          onClick={() => { const next = [...dismissed, "__workspace__"]; sessionStorage.setItem("fixli_trial_banner_dismissed", next.join(",")); setDismissed(next); }}>
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }
  const ending = Object.entries(user.addon_trials || {})
    .filter(([k, t]) => t?.status === "active" && t.days_left != null && t.days_left <= LAST_DAYS && !dismissed.includes(k))
    .sort((a, b) => a[1].days_left - b[1].days_left);
  if (!ending.length) return null;
  const [key, t] = ending[0];
  const others = ending.length - 1;
  const when = t.ends_at ? new Date(t.ends_at).toLocaleDateString("en-GB") : "";
  const days = t.days_left === 1 ? "tomorrow" : `in ${t.days_left} days`;
  return (
    <div className="bg-amber-500 text-amber-950 text-xs sm:text-sm px-4 py-2 flex items-center gap-3 flex-wrap" data-testid="trial-ending-banner">
      <Timer size={16} weight="bold" className="shrink-0" />
      <span className="flex-1 min-w-0">
        <strong>{featureLabel(key)}</strong> free trial ends {days} ({when}){others > 0 ? ` — and ${others} other trial${others > 1 ? "s" : ""} soon after` : ""}.
        {isAdmin ? " Add it to your plan to keep it." : " Ask your workspace admin to add it to the plan."}
      </span>
      {isAdmin && (
        <Link to={`/platform/billing?add=${key}`} className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline" data-testid="trial-ending-banner-cta">
          Add to plan <ArrowRight size={12} weight="bold" />
        </Link>
      )}
      <button type="button" className="p-1 rounded hover:bg-amber-600/40" aria-label="Dismiss" data-testid="trial-ending-banner-dismiss"
        onClick={() => { const next = [...dismissed, key]; sessionStorage.setItem("fixli_trial_banner_dismissed", next.join(",")); setDismissed(next); }}>
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
